import { useRef, useState } from 'react';
import { examTypes, subjects } from '@/mocks/examData';

interface PdfUploadPanelProps {
  isTeacher: boolean;
  studentName: string;
  studentSuggestions: string[];
  examName: string;
  subject: string;
  examType: string;
  onExamChange: (patch: { studentName?: string; examName?: string; subject?: string; examType?: string }) => void;
  onFileSelected: (file: File) => void;
  onErrorChange: (message: string | null) => void;
  onClearPdf: () => void;
  fileName: string | null;
  loading: boolean;
  error: string | null;
  capturedCount: number;
  noteCount: number;
  onGoToNotes: () => void;
}

export default function PdfUploadPanel(props: PdfUploadPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f: File | undefined | null) => {
    if (!f) return;
    props.onFileSelected(f);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const fieldClass =
    'w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100';
  const labelClass = 'text-xs font-semibold text-foreground-600';
  const missingStudent = props.isTeacher && props.studentName.trim() === '';
  const steps = props.isTeacher
    ? [
        '맨 아래에서 학생 이름을 먼저 입력해요.',
        '시험지 PDF를 업로드하면 오른쪽에 시험지가 열려요.',
        '오답노트에 넣을 문제 위에서 손가락(마우스)으로 사각형을 그려요.',
        '손을 떼면 그 영역이 바로 그 학생의 오답노트에 들어가요.',
      ]
    : [
        '시험지 PDF를 업로드하면 오른쪽에 시험지가 열려요.',
        '오답노트에 넣을 문제 위에서 손가락(마우스)으로 사각형을 그려요.',
        '손을 떼면 그 영역이 바로 오답노트 문제칸에 들어가요.',
      ];

  return (
    <div className="flex flex-col gap-5">
      {/* 파일 업로드 */}
      <div>
        <p className={`${labelClass} mb-2`}>시험지 업로드 (PDF)</p>

        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`group flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-7 text-center transition ${
            dragOver
              ? 'border-primary-400 bg-primary-50'
              : 'border-background-300 bg-background-100/60 hover:border-primary-300 hover:bg-primary-50/50'
          }`}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-upload-cloud-2-line text-xl"></i>
          </span>
          <p className="text-sm font-medium text-foreground-800">눌러서 PDF를 선택하세요</p>
          <p className="text-xs text-foreground-500">PDF가 열리면 문제 영역을 손가락(마우스)으로 끌어 캡처해요</p>
          <span className="mt-1 flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-bold text-background-50">
            <i className="ri-file-pdf-2-line text-sm"></i>
            PDF 파일 선택
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (!picked) {
                props.onErrorChange('파일을 가져오지 못했어요. PDF를 다시 선택해 주세요.');
              } else {
                handleFile(picked);
              }
              e.target.value = '';
            }}
          />
        </label>

        {props.loading && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-3 text-sm text-foreground-700">
            <i className="ri-loader-4-line animate-spin text-base text-primary-600"></i>
            PDF를 여는 중이에요...
          </div>
        )}

        {props.error && (
          <div className="mt-3 rounded-lg border border-accent-200 bg-accent-50 p-3 text-sm text-accent-800">
            <i className="ri-error-warning-line mr-1"></i>
            {props.error}
          </div>
        )}

        {props.fileName && !props.loading && (
          <div className="mt-3 flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-file-pdf-2-line text-2xl"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground-800">{props.fileName}</p>
              <p className="text-xs text-foreground-500">캡처 {props.capturedCount}문제</p>
            </div>
            <button
              type="button"
              onClick={props.onClearPdf}
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-foreground-700"
              aria-label="PDF 닫기"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        )}
      </div>

      {/* 사용 방법 */}
      <div className="rounded-lg border border-secondary-200 bg-secondary-50/70 p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-secondary-900">
          <i className="ri-magic-line text-sm"></i>
          이렇게 사용하세요
        </p>
        <ol className="flex flex-col gap-1.5 text-[11px] leading-relaxed text-foreground-600">
          {steps.map((step, index) => (
            <li key={step} className="flex gap-1.5">
              <span className="font-bold text-secondary-700">{index + 1}.</span>
              {step}
            </li>
          ))}
        </ol>
      </div>

      {/* 시험 정보 */}
      <div>
        <p className={`${labelClass} mb-2`}>
          {props.isTeacher ? '학생 · 시험 정보' : '시험 정보'} (캡처되는 노트에 함께 저장돼요)
        </p>
        {props.isTeacher && (
          <div className="mb-3">
            <label className="mb-1 flex items-center gap-1 text-xs text-foreground-500">
              학생 이름 <span className="font-bold text-accent-600">*</span>
            </label>
            <input
              className={`${fieldClass} ${
                missingStudent ? 'border-accent-300 focus:border-accent-400 focus:ring-accent-100' : ''
              }`}
              value={props.studentName}
              onChange={(e) => props.onExamChange({ studentName: e.target.value })}
              placeholder="예: 홍길동"
              list="student-name-options"
            />
            <datalist id="student-name-options">
              {props.studentSuggestions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
            {missingStudent && (
              <p className="mt-1.5 flex items-center gap-1 text-[11px] text-accent-700">
                <i className="ri-error-warning-line text-xs"></i>
                캡처하려면 학생 이름을 먼저 입력해 주세요.
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-foreground-500">시험 유형</label>
            <select className={fieldClass} value={props.examType} onChange={(e) => props.onExamChange({ examType: e.target.value })}>
              {examTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-foreground-500">과목</label>
            <select className={fieldClass} value={props.subject} onChange={(e) => props.onExamChange({ subject: e.target.value })}>
              {subjects.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="mb-1 block text-xs text-foreground-500">시험명</label>
          <input
            className={fieldClass}
            value={props.examName}
            onChange={(e) => props.onExamChange({ examName: e.target.value })}
            placeholder="예: 2025학년도 수능 수학"
          />
        </div>
      </div>

      {/* 오답노트로 이동 */}
      <button
        type="button"
        onClick={props.onGoToNotes}
        className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
      >
        <i className="ri-file-text-line text-base"></i>
        오답노트 확인하기
        <span className="rounded-full bg-background-50/25 px-2 py-0.5 text-xs font-bold">{props.noteCount}</span>
      </button>
    </div>
  );
}